import ArrowRightIcon from "@sprout/icons/arrow_right";
import "./profile_card.scss";
import { Body, SectionTitle, Link } from "@/sprout/typography";
import { Link as InertiaLink, usePage } from "@inertiajs/react";
import useTranslation from "@hooks/use_translation";
import CompletionTag from "@components/completion_tag.tsx";
import { CompletionStatus } from "@utils/completion_status.ts";
import useProfile from "@/queries/use_profile";
import ClearVerifiedBadge from "@/components/ui/clear_verified_badge";
import LoadingSpinner from "@/sprout/loading_spinner";
import { SecondaryButtonLink } from "@sprout/buttons";
import { useDashboardPreview } from "@components/dashboard/preview_context";
import WorkPreferencesWidget from "@components/dashboard/widgets/work_preferences/work_preferences_widget.tsx";
import ResumeUploadBox from "@components/dashboard/resume_upload_box";
import { Option } from "@sprout/select";
import { useState } from "react";

const ProfileCardHeader = () => {
  const { t } = useTranslation("user");
  return (
    <InertiaLink href="/profile" className="profile_card__header">
      <SectionTitle>{t("profile")}</SectionTitle>
      <div className="profile_card__header__icon">
        <ArrowRightIcon />
      </div>
    </InertiaLink>
  );
};

type Props = {
  quickApplyPath: string;
  workPreferencesPath: string;
  employmentTypes: Option[];
  worksites: Option[];
};

const ProfileCard = ({
  quickApplyPath,
  workPreferencesPath,
  employmentTypes,
  worksites,
}: Props) => {
  const { t } = useTranslation("user");
  const { preview, email: previewEmail } = useDashboardPreview();
  const { data, isLoading, error } = useProfile({ mock: preview });
  const [widgetDismissed, setWidgetDismissed] = useState(
    !!localStorage.getItem("workPreferencesWidgetDismissedAt"),
  );

  if (isLoading) {
    return (
      <div className="profile_card">
        <ProfileCardHeader />
        <div className="profile_card__content">
          <div className="flex justify-center items-center h-32">
            <LoadingSpinner size="medium" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="profile_card">
        <ProfileCardHeader />
        <div className="profile_card__content">
          <Body>{t("defaultError")}</Body>
        </div>
      </div>
    );
  }

  const {
    email,
    firstName,
    lastName,
    quickApplyCompletion,
    workPreferencesCompletion,
    isClearVerified,
  } = data;

  const hasAnyName = Boolean(firstName || lastName);

  const { uploadResumeModal, profileWidgetEnhancement } = usePage<{
    uploadResumeModal?: boolean;
    profileWidgetEnhancement?: boolean;
  }>().props;

  let profileContent = null;

  if (profileWidgetEnhancement) {
    const hasResume = Boolean(data.resume);
    if (hasResume && hasAnyName) {
      const initials = [firstName, lastName]
        .filter(Boolean)
        .map((n) => n![0].toUpperCase())
        .join("");
      profileContent = (
        <div className="flex items-center gap-3">
          <div className="profile_card__avatar">{initials}</div>
          <div>
            <div className="flex items-center gap-2">
              <Body className="name">
                {firstName} {lastName}
              </Body>
              {isClearVerified && <ClearVerifiedBadge />}
            </div>
            {data.location && <Body className="location">{data.location.display_name}</Body>}
          </div>
        </div>
      );
    } else if (!hasResume) {
      profileContent = (
        <div className="flex flex-col gap-2">
          <Body className="upload-label">{t("uploadYourResume")}</Body>
          <ResumeUploadBox />
        </div>
      );
    }
  } else if (hasAnyName) {
    profileContent = (
      <div>
        <div className="flex items-center gap-2">
          <Body>
            {firstName} {lastName}
          </Body>
          {isClearVerified && <ClearVerifiedBadge />}
        </div>
        <div>
          <Body className="profile_card__email">{email}</Body>
        </div>
      </div>
    );
  } else {
    profileContent = (
      <div className="flex items-center gap-2">
        <Body>{email || (preview && previewEmail)}</Body>
        {isClearVerified && <ClearVerifiedBadge />}
      </div>
    );
  }

  const GetStartedButton = ({ href }: { href: string }) => (
    <SecondaryButtonLink href={href} size="medium">
      {t("getStarted")}
    </SecondaryButtonLink>
  );

  const linkClassName = uploadResumeModal ? "link--muted" : "";

  const NotStartedStatus = ({ href }: { href: string }) => {
    if (uploadResumeModal) {
      return <CompletionTag status={CompletionStatus.NotStarted} />;
    }
    return <GetStartedButton href={href} />;
  };

  const showWorkPreferencesWidget =
    !widgetDismissed && data.workPreferencesCompletion === CompletionStatus.NotStarted;

  return (
    <div className="profile_card">
      <ProfileCardHeader />
      <div className="profile_card__content">
        {profileContent}
        <hr />
        <div className="profile_card__footer">
          <div>
            <Link href={quickApplyPath} className={linkClassName}>
              {t("quickApply")}
            </Link>
            {quickApplyCompletion === CompletionStatus.NotStarted ? (
              <NotStartedStatus href={quickApplyPath} />
            ) : (
              <CompletionTag status={quickApplyCompletion} />
            )}
          </div>
          {showWorkPreferencesWidget ? (
            <WorkPreferencesWidget
              workPreferencesPath={workPreferencesPath}
              employmentTypes={employmentTypes}
              worksites={worksites}
              onDismiss={() => setWidgetDismissed(true)}
            />
          ) : (
            <div>
              <Link href={workPreferencesPath} className={linkClassName}>
                {t("workPreferences.title")}
              </Link>
              {workPreferencesCompletion === CompletionStatus.NotStarted ? (
                <NotStartedStatus href={workPreferencesPath} />
              ) : (
                <CompletionTag status={workPreferencesCompletion} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
